// @ts-check
import { gql } from '@apollo/client/core';
import React from 'react';
import { Company, Flex, Page, Product, Text } from '../../pdf-design-system';
import { PagesProvider } from '../../pdf-design-system/Base/PageLayout/Page';
import openSansLight from '../../pdf-design-system/fonts/open-sans/300';
// import openSansBold from '../../pdf-design-system/fonts/open-sans/600';
import sourceSansRegular from '../../pdf-design-system/fonts/source-sans-pro/400';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
// import stencil from './images/stencil1.png';
import messages from './locale/messages';
import RoussillonCatalogProfilePage from './RoussillonCatalogProfilePage';
import RoussillonCatalogWinePage from './RoussillonCatalogWinePage';

export default function RoussillonCatalog({ data, locale, ...rest }) {
  const { collection } = data;
  const sections = afterQuery(data, rest);
  const sectionsMap = sections.reduce((acc, section) => {
    const registeredProducts = section.registration?.registeredProducts?.nodes;
    const pageCount = Math.ceil(registeredProducts?.length / 3);
    if (pageCount === 0) {
      acc.push({ section, pages: [] });
      return acc;
    }
    const pages = Array.from(Array(pageCount)).map((_, index) => {
      const products = registeredProducts?.slice(index * 3, index * 3 + 3);
      return {
        registration: {
          ...section.registration,
          registeredProducts: { nodes: products },
        },
      };
    });
    acc.push({ section, pages });
    return acc;
  }, []);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {sectionsMap.map(({ section, pages }) => (
          <React.Fragment>
            <RoussillonCatalogProfilePage
              page={section}
              theme={theme}
              baseUrl={collection?.site?.publicUrl}
            />
            {pages.map((page, index) => (
              <RoussillonCatalogWinePage
                key={index}
                page={page}
                theme={theme}
                baseUrl={collection?.site?.publicUrl}
                {...rest}
              />
            ))}
            {pages.length === 0 && (
              <PageLayout>
                <Flex
                  style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexGrow: 1,
                  }}
                >
                  <Text>Your wines will appear here after you enter them.</Text>
                </Flex>
              </PageLayout>
            )}
          </React.Fragment>
        ))}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

export function PageLayout({ theme, bleed = 3, style, children, ...rest }) {
  return (
    <Page
      size={{ width: `${148 + 2 * bleed}mm`, height: `${210 + 2 * bleed}mm` }}
      style={[
        { paddingVertical: `${bleed}mm`, paddingHorizontal: `${bleed}mm` },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {children}
    </Page>
  );
}

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

const colors = {
  primary: '#c39e6d',
  accent: '#c39e6d',
};

/** @type {Theme} */
export const theme = {
  colors,
  components: {
    Text: {
      base: { ...sourceSansRegular, fontSize: '10.5pt' },
      default: { color: '#000' },
      small: { fontSize: 9 },
      light: { color: '#888' },
    },
    Title: {
      base: { ...openSansLight },
      small: {
        fontSize: 10,
        lineHeight: 1,
      },
      default: {
        fontSize: '14pt',
        lineHeight: 0.95,
      },
      large: {
        fontSize: 18,
        lineHeight: 1.3,
      },
    },
  },
};

RoussillonCatalog.fragment = gql`
  fragment RoussillonCatalog on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...RoussillonCatalog_Table
    }
  }

  fragment RoussillonCatalog_Table on RegistrationCatalogSection {
    registration {
      registrationId
      name
      companyId
      stand {
        name
      }

      profile {
        name
        street
        postalCode
        city
        country
        telephone
        email
        website
        shortDescription
        ...ProfileLogo
        autoSizedLogo: logo {
          # 55mm in 300dpi
          fixed(width: 654, crop: PAD, gravity: NORTH, fetch_format: JPG) {
            src
          }
        }
        mainImage {
          url
        }
      }
      attendingContacts {
        fullName
      }

      registeredProducts {
        nodes {
          producer {
            isSeekingDistribution
            distributionContacts {
              contactId
              companyName
            }
          }
          pricing {
            currency
            priceType
            price
          }
          productId
          product {
            productId
            name
            alcohol
            # description
            regionHierarchy
            denomination
            ...Blend
            ...BottleImage
            ... on Wine {
              vineyard
              designation
              residualSugar
              totalAcidity
            }
          }
        }
      }
    }
  }
  ${Product.Blend.fragment}
  ${Product.BottleImage.fragment}
  ${Company.ProfileLogo.fragment}
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}
